import { toLonLat } from 'ol/proj';
import { v4 as uuidv4 } from 'uuid';
import ImageLayer from 'ol/layer/Image';
import cloneDeep from 'lodash.clonedeep';
import { Geometry, Polygon } from 'ol/geom';
import GeoImage from 'ol-ext/source/GeoImage';
import { useLocalStorage, useToggle } from 'usehooks-ts';
import { matchPath, useLocation, useParams } from 'react-router-dom';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';

import Toolbar from './toolbar';
import { MapConfigStore } from '.';
import {
  UpdateZone,
  GetTaskById,
  GetZoneById,
  GetMapByIdAsync,
  UpdateMapAsync,
  UpdateZoneAsync,
  UpdateAreaAsync,
  UpdateTaskAsync,
  GetTasksByZoneId,
  AddResourceAsync,
  GetAreasByZoneId,
  GetResourceValue,
  AddAreaEntityAsync,
  AddTaskEntityAsync,
  AddZoneEntityAsync,
  UpdateGameDocState
} from '../../utils/game-document';
import {
  DEFAULT_LATITUDE,
  DEFAULT_LONGITUDE
} from '../../constants/coordinate';
import {
  MapEntity,
  ZoneEntity,
  AreaEntity,
  TaskEntity,
  ResourceEntity
} from '../../types/game-document/';
import { Coordinate } from '../../types/place';
import { uuid } from '../../types/common-helper';
import { MapImage } from '../../types/game-document';
import MapEditor from '../../components/map/map-editor';
import MapCanvas from '../../components/map/map-canvas';
import { useGameTask } from '../../hooks/use-game-task';
import { useBreadcrumb } from '../../contexts/breadcrumb';
import { useGameRoute } from '../../hooks/use-game-route';
import { MapContext } from '../../components/map/map-context';
import { GameDocumentContext } from '../../contexts/game-document';
import { ZoneEditorWindow } from '../../features/game-document/zones';
import { TaskEditorWindow } from '../../features/game-document/tasks';
import { AreaEditorWindow } from '../../features/game-document/areas';
import { ZoneAssetId } from '../../types/game-document/entities/zone';
import { EntityEditor } from '../../types/game-document/entity-editor';
import { repositionImageToMap } from '../../utils/map/objects-position';
import MapIllustrationCanvas from '../../components/map/map-illustration-canvas';
import { UpdateMapImageAsync } from '../../utils/game-document/assets/map-image';

export type MapsOptions = 'gis' | 'illustrated';
export type MapAction = 'Zone' | 'Task' | 'Area' | 'Annotation' | 'Images' | '';
/**
 *
 * @returns WorldMap
 */
const GameMapZone = () => {
  const { gameId } = useParams();
  const [mapConfig] = useLocalStorage<MapConfigStore>(
    `map-config-${gameId}`,
    {}
  );

  const [state, setState] = useContext(GameDocumentContext);
  const map = useContext(MapContext);
  const titleGuid = uuid();
  const { zoneId } = useParams();
  const location = useLocation();
  const [coordinate, setCoordinate] = useState<Coordinate>();
  const [activeAddZoneState, setActiveAddZoneState] = useState<boolean>(false);
  const [selectAllAssets, setSelectAllAssets] = useState<string>('');
  const [entityZoneEditorIsVisible, setEntityZoneEditorIsVisible] =
    useState<boolean>(false);
  const toggleZoneEntityEditor = () => {
    setEntityZoneEditorIsVisible(!entityZoneEditorIsVisible);
  };

  const [entityAreaEditorIsVisible, setEntityAreaEditorIsVisible] =
    useState<boolean>(false);
  const toggleAreaEntityEditor = () => {
    setEntityAreaEditorIsVisible(!entityAreaEditorIsVisible);
  };

  const [entityTaskEditorIsVisible, setEntityTaskEditorIsVisible] =
    useState<boolean>(false);
  const toggleTaskEntityEditor = () => {
    setEntityTaskEditorIsVisible(!entityTaskEditorIsVisible);
  };
  const [entityTaskEditorValue, setEntityTaskEditorValue] =
    useState<EntityEditor<TaskEntity>>();

  const [entityZoneEditorValue, setEntityZoneEditorValue] =
    useState<EntityEditor<ZoneEntity>>();

  const [resourceEditorValue, setResourceEditorValue] =
    useState<EntityEditor<ResourceEntity>[]>();

  const [isClearInteractions, setIsClearInteractions] =
    useState<boolean>(false);

  const [mapAssetId, setMapAssetId] = useState<string>('');
  const [mapZoneTitleName, setMapZoneTitleName] = useState<string>('');

  const { gameDocument } = state;
  const [assetMap, setAssetMap] = useState<MapEntity>();

  const [tempZone, setTempZone] = useState<ZoneEntity>({
    id: '',
    name: '',
    description: ''
  });
  const [tempArea, setTempArea] = useState<AreaEntity>({
    id: '',
    name: '',
    description: ''
  });
  const [tempTask, setTempTask] = useState<TaskEntity>({
    id: '',
    name: '',
    description: ''
  });

  const [selectedPanel, setSelectedPanel] = useState<MapAction>('');

  const [mapInfo, setMapInfo] = useState<MapEntity>();
  const [isShowZoneNameVisibility, setIsShowZoneNameVisibility] =
    useState<boolean>(
      state.gameDocument?.overview?.showZonenameVisibility ?? false
    );
  const [isShowAreaNameVisibility, setIsShowAreaNameVisibility] =
    useState<boolean>(
      state.gameDocument?.overview?.showAreanameVisibility ?? false
    );
  const [vectorPopupId, setVectorPopupId] = useState<string>();

  const [showAllZones, setShowAllZones] = useToggle();
  const [showAllAreas, setShowAllAreas] = useToggle();
  const [showTaskTitle, toggleShowTaskTitle] = useToggle(
    mapConfig.showTaskTitle !== undefined ? mapConfig.showTaskTitle : true
  );

  const { setBreadcrumb } = useBreadcrumb();

  const defaultTaskAvailableIconResId =
    state.gameDocument?.settings.designer?.defaultTaskAvailableIconResId || '';
  const defaultTaskCompletedIconResId =
    state.gameDocument?.settings.designer?.defaultTaskCompletedIconResId || '';

  const entity: TaskEntity = {
    id: '',
    name: '',
    description: '',
    titleResId: titleGuid,
    imageResId: defaultTaskAvailableIconResId,
    completeImageResId: defaultTaskCompletedIconResId,
    hideWhenComplete:
      state.gameDocument && !state.gameDocument.settings.designer
        ? false
        : !Boolean(
            state.gameDocument?.settings.designer?.taskCompletionVisibility
          )
  };

  const titleResource: ResourceEntity = {
    id: titleGuid,
    name: 'Title',
    description: '',
    type: 'text',
    value: ''
  };

  //Zones
  const setToBrowserLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((pos) => {
        setCoordinate({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude
        });
      });
    } else {
      alert('Geolocation is not supported by this browser.');
    }
  };

  const getMapInfo = (mapAsset: MapEntity) => {
    let center = map.getView().getCenter();
    if (
      center === undefined ||
      center[0] === undefined ||
      center[1] === undefined
    ) {
      return undefined;
    }

    const mapEntity: MapEntity = {
      titleResId: mapAsset.id,
      id: mapAsset.id!,
      longitude: mapAsset.longitude,
      latitude: mapAsset.latitude,
      name: mapAsset.name || '',
      description: mapAsset.description || '',
      type: mapAsset.type!,
      zoomLevel: mapAsset.zoomLevel,
      imageResId: mapAsset.imageResId ?? ''
    };

    if (mapEntity.type === 'illustrated') mapEntity.zoomLevel = 1;
    setMapInfo(mapEntity);
  };

  const zones = useMemo(() => {
    if (!gameDocument || !zoneId) return [];

    return (
      gameDocument.assets.zones?.filter((zone) => zone.id === zoneId) ?? []
    );
  }, [gameDocument, zoneId]);

  const areas = useMemo(() => {
    if (!gameDocument || !zoneId) return [];

    return GetAreasByZoneId(gameDocument, zoneId);
  }, [gameDocument, zoneId]);

  const tasks = useMemo(() => {
    if (!gameDocument || !zoneId) return [];

    return GetTasksByZoneId(zoneId, gameDocument);
  }, [gameDocument, zoneId]);

  const routes = useMemo(() => {
    if (!gameDocument) return [];

    const allRoute = state?.gameDocument?.assets?.routes ?? [];
    if (mapAssetId) {
      return allRoute.filter((item) => item.mapId === mapAssetId);
    } else {
      return allRoute;
    }
  }, [gameDocument]);

  const handleZoneEntityEditorSubmit = async (
    editorEntity: EntityEditor<ZoneEntity>
  ) => {
    editorEntity.entity.boundary = tempZone.boundary;
    editorEntity.entity.id = uuidv4();
    editorEntity.entity.isVisible = true;
    if (state.gameDocument) {
      let result = await AddZoneEntityAsync(
        state.gameDocument!,
        editorEntity.entity
      );
      if (result) {
        result.rules.worldMap.zones.push({
          zoneAssId: editorEntity.entity.id,
          timerAssId: state.gameDocument.rules.worldMap.timerAssId
        });
        setState((state) => UpdateGameDocState(state, result));
        setEntityZoneEditorIsVisible(false);
      }
    }
  };

  const onAddZone = (zone: ZoneEntity) => {
    setTempZone({
      id: '',
      name: '',
      description: '',
      boundary: {
        id: uuidv4(),
        type: 'Feature',
        geometry: {
          type: zone.boundary?.type ?? 'Polygon',
          coordinates: zone.boundary?.geometry as any
        }
      }
    });

    setEntityZoneEditorValue({
      isNew: true,
      entity: { id: '', name: '', description: '' }
    });
    toggleZoneEntityEditor();
    setActiveAddZoneState(!activeAddZoneState);
  };

  const onAddArea = (area: AreaEntity) => {
    setTempArea({
      id: uuidv4(),
      name: '',
      description: '',
      boundary: {
        id: uuidv4(),
        type: 'Feature',
        geometry: {
          type: area.boundary?.type ?? 'Polygon',
          coordinates: area.boundary?.geometry as any
        }
      }
    });

    toggleAreaEntityEditor();
  };

  const handleAreaEntityEditorSubmit = async (
    editorEntity: EntityEditor<AreaEntity>,
    resourceEntity: EntityEditor<ResourceEntity>[]
  ) => {
    editorEntity.entity.boundary = tempArea.boundary;
    editorEntity.entity.id = uuidv4();
    editorEntity.entity.isVisible = true;

    if (state.gameDocument && zoneId) {
      AddResourceAsync(
        state.gameDocument!,
        `${editorEntity.entity.id}-title`,
        'Area title',
        'text',
        resourceEntity.find(
          (res) => res.entity.id === editorEntity.entity.titleResId!
        )?.entity.value!,
        editorEntity.entity.titleResId
      ).then((response) => {
        setState((state) => UpdateGameDocState(state, response!));
      });

      await AddAreaEntityAsync(state.gameDocument, editorEntity.entity).then(
        (updatedGameDocument) => {
          let zone = GetZoneById(updatedGameDocument, zoneId);

          if (zone) {
            if (zone?.areas === undefined) {
              zone.areas = [];
            }

            zone.areas.push({ areaAssId: editorEntity.entity.id });

            UpdateZoneAsync(updatedGameDocument, zoneId, zone).then(
              (updatedGameDocument) => {
                setState((prev) =>
                  UpdateGameDocState(prev, updatedGameDocument)
                );
              }
            );
            toggleAreaEntityEditor();
          }
        }
      );
    }
  };
  /**
   * Handle when area resize, move or edit
   * @param area
   */
  const handleEditAreaChange = (id: string, geometry: Geometry) => {
    setState((prev) => {
      const newCoordinate = (geometry as Polygon).getCoordinates();
      const areas = prev.gameDocument?.assets.areas ?? [];
      const selectedArea = areas.find((a) => a.boundary.id === id);

      if (selectedArea) {
        selectedArea.boundary.geometry.coordinates = newCoordinate;
        const resp = UpdateAreaAsync(prev.gameDocument!, id, selectedArea);
        return UpdateGameDocState(prev, resp);
      }

      return prev;
    });
  };

  /**
   * Add task marker/pin from existing task
   * @param taskId
   */
  const handleSelectedExistingTask = (taskId: string) => {
    let task = GetTaskById(state?.gameDocument!, taskId);
    if (task) {
      task!.boundary = tempTask.boundary;
      task.isVisible = true;

      UpdateTaskAsync(state?.gameDocument!, task.id, task).then((result) => {
        if (zoneId) {
          let zone = GetZoneById(result, zoneId);

          if (zone) {
            if (zone?.tasks === undefined) {
              zone.tasks = [];
            }
            zone.tasks.push({ taskAssId: task!.id });
            UpdateZoneAsync(state.gameDocument!, zoneId, zone).then((res) => {
              setState((prev) => UpdateGameDocState(prev, res));
              setEntityTaskEditorIsVisible(false);
            });
          }
        }
        setState((prev) => UpdateGameDocState(prev, result));
        setEntityTaskEditorIsVisible(false);
      });
    }
  };

  const handleTaskEntityEditorSubmit = async (
    editorEntity: EntityEditor<TaskEntity>,
    resourceEntity: EntityEditor<ResourceEntity>[]
  ) => {
    const defaultTaskAvailableIconResId =
      state.gameDocument?.settings.designer?.defaultTaskAvailableIconResId ||
      '';
    const defaultTaskCompletedIconResId =
      state.gameDocument?.settings.designer?.defaultTaskCompletedIconResId ||
      '';

    const promises = [];

    const addTitleResource = AddResourceAsync(
      state.gameDocument!,
      'Title',
      '',
      'text',
      resourceEntity.find(
        (task) => task.entity.id === editorEntity.entity.titleResId!
      )?.entity.value!,
      editorEntity.entity.titleResId
    );
    promises.push(addTitleResource);

    if (
      editorEntity.entity.imageResId &&
      editorEntity.entity.imageResId !== defaultTaskAvailableIconResId
    ) {
      const imageResource = resourceEntity.find(
        (task) => task.entity.id === editorEntity.entity.imageResId!
      )?.entity;
      if (imageResource) {
        const addImageResource = AddResourceAsync(
          state.gameDocument!,
          imageResource.name || editorEntity.entity.imageResId!,
          imageResource.description ?? '',
          'image',
          imageResource.value ?? '',
          editorEntity.entity.imageResId!,
          imageResource.size ?? 0
        );
        promises.push(addImageResource);
      }
    }

    if (
      editorEntity.entity.completeImageResId &&
      editorEntity.entity.completeImageResId !== defaultTaskCompletedIconResId
    ) {
      const completeImageResource = resourceEntity.find(
        (task) => task.entity.id === editorEntity.entity.completeImageResId!
      )?.entity;
      if (completeImageResource) {
        const addCompleteImageResource = AddResourceAsync(
          state.gameDocument!,
          completeImageResource.name || editorEntity.entity.completeImageResId!,
          completeImageResource.description ?? '',
          'image',
          completeImageResource.value ?? '',
          editorEntity.entity.completeImageResId!,
          completeImageResource.size ?? 0
        );
        promises.push(addCompleteImageResource);
      }
    }

    Promise.all(promises).then(() => {
      editorEntity.entity.boundary = tempTask.boundary;
      editorEntity.entity.id = uuidv4();
      editorEntity.entity.isVisible = true;
      if (state.gameDocument) {
        AddTaskEntityAsync(state.gameDocument!, editorEntity.entity).then(
          (result) => {
            if (zoneId) {
              let zone = GetZoneById(result, zoneId);

              if (zone) {
                if (zone?.tasks === undefined) {
                  zone.tasks = [];
                }
                zone.tasks.push({ taskAssId: editorEntity.entity.id });
                UpdateZoneAsync(state.gameDocument!, zoneId, zone).then(
                  (res) => {
                    setState((prev) => UpdateGameDocState(prev, res));
                    setEntityTaskEditorIsVisible(false);
                  }
                );
              }
            }
          }
        );
      }
    });
  };

  const onAddTask = (coordinates: [number, number]) => {
    setTempTask({
      id: '',
      name: '',
      description: '',
      boundary: {
        id: uuidv4(),
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates
        }
      }
    });

    setEntityTaskEditorValue({
      isNew: true,
      entity: entity
    });

    setResourceEditorValue([
      {
        isNew: true,
        entity: titleResource
      }
    ]);

    toggleTaskEntityEditor();
    setActiveAddZoneState(!activeAddZoneState);
  };

  /**
   * Handle when zone resize, move or edit
   * @param worldZones
   */
  const handleEditZoneChange = (id: string, geometry: Geometry) => {
    setState((prev) => {
      const newCoordinate = (geometry as Polygon).getCoordinates();
      const zones = prev.gameDocument?.assets.zones ?? [];
      const selectedZone = zones.find((a) => a.boundary.id === id);

      if (selectedZone) {
        selectedZone.boundary.geometry.coordinates = newCoordinate;
        const resp = UpdateZone(prev.gameDocument!, id, selectedZone);
        return UpdateGameDocState(prev, resp);
      }

      return prev;
    });
  };

  const updateMapAsset = async (map: MapEntity, imageResId?: string) => {
    UpdateMapAsync(state?.gameDocument!, map.id, map).then((response) => {
      let updatedGameDocument = cloneDeep(response);

      if (imageResId && zoneId) {
        const imgDimention = { width: 0, height: 0 };
        const img = new Image();
        img.src = GetResourceValue(response, imageResId);
        img.onload = () => {
          imgDimention.width = img.width;
          imgDimention.height = img.height;

          const mapElement = document.getElementById('map');
          let mapSize = { width: 0, height: 0 };

          if (mapElement) {
            mapSize.width = mapElement.clientWidth;
            mapSize.height = mapElement.clientHeight;
          }

          const { tasks: tasksReposition, areas: areasReposition } =
            repositionImageToMap(
              updatedGameDocument.assets,
              map.longitude ?? DEFAULT_LONGITUDE,
              map.latitude ?? DEFAULT_LATITUDE,
              8,
              [mapSize.width, mapSize.height],
              imgDimention.width,
              imgDimention.height
            );

          const taskIds = tasks.map((t) => t.id);
          const areaIds = areas.map((a) => a.id);

          const filteredAreas = areasReposition.filter((a) =>
            areaIds.includes(a.id)
          );
          const filteredTasks = tasksReposition.filter((t) =>
            taskIds.includes(t.id)
          );

          //@ts-ignore
          updatedGameDocument.assets = {
            ...response.assets,
            areas: [
              ...(response.assets.areas ?? []).filter(
                (a) => !areaIds.includes(a.id)
              ),
              ...filteredAreas
            ],
            tasks: [
              ...(response.assets.tasks ?? []).filter(
                (t) => !taskIds.includes(t.id)
              ),
              ...filteredTasks
            ]
          };

          setState((prev) => UpdateGameDocState(prev, updatedGameDocument));
        };
      } else {
        setState((prev) => UpdateGameDocState(prev, updatedGameDocument));
      }

      setAssetMap(map);
    });
  };

  const calculateCenter = (geometry: any) => {
    let centerZone: any, coordinates;

    let x = 0;
    let y = 0;
    let i = 0;
    coordinates = geometry.coordinates[0];
    coordinates.forEach(function (coordinate: any) {
      x += coordinate[0];
      y += coordinate[1];
      i++;
    });
    centerZone = [x / i, y / i];
    return centerZone;
  };

  const onChangeMapOptions = async (MapsOptions: MapsOptions) => {
    if (mapAssetId !== undefined && mapAssetId !== '') {
      let mapEntity = await GetMapByIdAsync(state?.gameDocument!, mapAssetId);
      if (mapEntity) {
        if (MapsOptions === 'illustrated') {
          mapEntity.type = 'illustrated';
          mapEntity.zoomLevel = undefined;
          mapEntity.longitude = undefined;
          mapEntity.latitude = undefined;
          await updateMapAsset(mapEntity);
        } else {
          const selectedZone = zones.find((z) => z.id === zoneId);
          if (selectedZone) {
            const centerZone = calculateCenter(selectedZone.boundary.geometry);
            const [lng, lat] = toLonLat(centerZone);
            const imageResId = mapEntity.imageResId;
            mapEntity.imageResId = '';
            mapEntity.type = 'googleMaps';
            mapEntity.longitude = lng;
            mapEntity.latitude = lat;

            await updateMapAsset(mapEntity, imageResId);
            setCoordinate({
              lat,
              lng
            });
          }
        }
      }
    }
  };

  const populateMapAsset = async (mapAssetId: string) => {
    let mapAsset = await GetMapByIdAsync(state?.gameDocument!, mapAssetId);
    if (mapAsset) {
      setAssetMap(mapAsset);
      getMapInfo(mapAsset);
    }
  };

  const handleEditTask = (task: TaskEntity) => {
    UpdateTaskAsync(state?.gameDocument!, task.id, task).then((response) => {
      setState((prev) => UpdateGameDocState(prev, response));
    });
  };

  const onSelectedPanel = (MapAction: MapAction) => {
    setSelectedPanel(MapAction);
  };

  const onEditMapImage = (mapImage: MapImage) => {
    UpdateMapImageAsync(state?.gameDocument!, mapImage.id, mapImage).then(
      (response) => {
        setState((prev) => UpdateGameDocState(prev, response));
      }
    );
  };

  useEffect(() => {
    document.title = `Maps - ${state.gameDocument?.name}`;
  }, [state.gameDocument?.name]);

  useEffect(() => {
    setIsClearInteractions(true);
  }, []);

  useEffect(() => {
    const zone = GetZoneById(gameDocument, zoneId!);
    if (zone) {
      const zoneRes = state.gameDocument?.resources.find(
        (item) => item.id === zone.titleResId!
      )! as ResourceEntity;
      if (zoneRes) {
        setMapZoneTitleName(zoneRes.value!);
      }
    }
    return () => setMapZoneTitleName('');
  }, [state.gameDocument?.assets.zones]);

  const mapImages = useMemo(() => {
    if (state.gameDocument) {
      if (zoneId) {
        return state?.gameDocument?.assets?.mapImages?.filter(
          (item) =>
            item.mapId ===
              state.gameDocument?.rules.worldMap.zones.find(
                (zone: ZoneAssetId) => zone.zoneAssId === zoneId
              )?.mapAssId || ''
        );
      } else {
        return (
          state?.gameDocument?.assets?.mapImages?.filter(
            (item) =>
              item.mapId === state?.gameDocument?.rules?.worldMap?.mapAssId!
          ) ?? []
        );
      }
    }
    return [];
  }, [state.gameDocument]);

  const getMapLabel = useMemo(() => {
    const zoneMapType = assetMap?.type;
    if (!zoneMapType) return 'gis';

    if (['googleMaps', 'openStreetMap', 'satelliteMap'].includes(zoneMapType))
      return 'gis';
    return 'illustrated';
  }, [assetMap]);

  const imageLayersRef = useRef<Record<string, ImageLayer<GeoImage>>>({});

  const updateOverlayImage = (
    id: string,
    newCenter: [number, number],
    newExtent: [number, number, number, number],
    newScale: [number, number] | undefined
  ) => {
    if (state.gameDocument && assetMap) {
      const source = imageLayersRef.current[id].getSource();
      if (source) {
        source.setCenter(newCenter);
        newScale && source.setScale(newScale);
      }

      const imageId = id.replaceAll('image-', '');
      const mapImage = mapImages?.find((image) => image.id === imageId);

      if (mapImage) {
        let result = cloneDeep(mapImage);
        result = {
          ...result,
          center: newCenter,
          extent: newExtent,
          ...(newScale !== undefined && {
            scale: newScale
          })
        };

        onEditMapImage(result);
      }
    }
  };

  const {
    taskPopupId,
    removeTaskOverlay,
    addTaskOverlay,
    updateTaskOverlayPosition
  } = useGameTask({
    selectAllAssets,
    tasks: tasks || [],
    zones: zones || [],
    areas: areas || [],
    mapImages: mapImages || [],
    mapType: assetMap?.type || 'googleMaps',
    showTaskTitle,
    updateOverlayImage
  });

  const {} = useGameRoute();

  useEffect(() => {
    const mapAssId = gameDocument?.rules.worldMap.zones.find(
      (z) => z.zoneAssId === zoneId
    )?.mapAssId;

    if (mapAssId) {
      setMapAssetId(mapAssId);
      populateMapAsset(mapAssId);
    }

    const selectedZone = zones.find((z) => z.id === zoneId);
    if (selectedZone) {
      const centerZone = calculateCenter(selectedZone.boundary.geometry);
      const [lng, lat] = toLonLat(centerZone);

      setCoordinate({
        lat,
        lng
      });
    }
  }, [zoneId, gameDocument]);

  const getZoneTitle = (zoneId: string | undefined) => {
    if (zoneId) {
      const zone = GetZoneById(state.gameDocument, zoneId);
      if (zone) {
        const title = GetResourceValue(state.gameDocument!, zone.titleResId!);
        return title === '' ? zone.name : title;
      }
    }
  };

  useEffect(() => {
    const breadcrumbPaths = [
      { path: '/designer/:gameId/map/*', text: 'Map' },
      { path: '/designer/:gameId/map/:zoneId/*', text: getZoneTitle(zoneId) }
    ];

    const newBreadcrumb = [];
    for (const config of breadcrumbPaths) {
      const match = matchPath(config.path, location.pathname);

      if (match) {
        newBreadcrumb.push({ text: config.text, to: match.pathnameBase });
      }
    }

    setBreadcrumb(newBreadcrumb);

    return () => {
      setBreadcrumb([]);
    };
  }, [setBreadcrumb, location.pathname]);

  return (
    <>
      <Toolbar title={mapZoneTitleName} />
      <div className={'map-designer'}>
        <div className={'layout__left'}>
          {(assetMap?.type === 'googleMaps' ||
            assetMap?.type === 'openStreetMap' ||
            assetMap?.type === 'satelliteMap') && (
            <MapCanvas
              key={zoneId}
              setVectorPopupId={setVectorPopupId}
              coordinate={coordinate}
              zones={zones}
              tasks={tasks}
              areas={areas}
              routes={routes}
              showAllZones={showAllZones}
              isShowZoneNameVisibility={isShowZoneNameVisibility}
              showAllAreas={showAllAreas}
              isShowAreaNameVisibility={isShowAreaNameVisibility}
              resourceImages={gameDocument?.resources}
              isClearInteractions={isClearInteractions}
              onEditZone={handleEditZoneChange}
              onAddZone={onAddZone}
              onAddTask={onAddTask}
              onAddArea={onAddArea}
              onEditArea={handleEditAreaChange}
              onEditTasks={handleEditTask}
              onEditMapImages={onEditMapImage}
              selectedPanel={selectedPanel}
              isEditZone={true}
              mapImages={mapImages}
              selectedObjectType={(selectAllAssets as any) ?? ''}
              onRemoveSelectAllAssets={() => setSelectAllAssets('')}
              addTaskOverlay={addTaskOverlay as any}
              removeTaskOverlay={removeTaskOverlay}
              updateTaskOverlayPosition={updateTaskOverlayPosition as any}
              imageLayersRef={imageLayersRef}
              updateOverlayImage={updateOverlayImage}
              mapInfo={mapInfo}
            />
          )}
          {assetMap?.type === 'illustrated' && (
            <MapIllustrationCanvas
              key={zoneId}
              zones={zones}
              tasks={tasks}
              setVectorPopupId={setVectorPopupId}
              showAllZones={showAllZones}
              isShowZoneNameVisibility={isShowZoneNameVisibility}
              onEditZone={handleEditZoneChange}
              onAddZone={onAddZone}
              onAddTask={onAddTask}
              resourceImages={gameDocument?.resources}
              url={GetResourceValue(
                state?.gameDocument!,
                assetMap!.imageResId!
              )}
              isClearInteractions={isClearInteractions}
              selectedPanel={selectedPanel}
              isEditZone={true}
              onEditTasks={handleEditTask}
              onEditMapImages={onEditMapImage}
              assetMap={assetMap}
              mapImages={state.gameDocument?.assets.mapImages?.filter(
                (item) => mapAssetId === item.mapId
              )}
              selectedObjectType={(selectAllAssets as any) ?? ''}
              onRemoveSelectAllAssets={() => setSelectAllAssets('')}
              addTaskOverlay={addTaskOverlay as any}
              removeTaskOverlay={removeTaskOverlay}
              updateTaskOverlayPosition={updateTaskOverlayPosition as any}
              imageLayersRef={imageLayersRef}
              updateOverlayImage={updateOverlayImage}
            />
          )}
        </div>

        <div className={'layout__right'}>
          <MapEditor
            key={zoneId}
            showTaskTitle={showTaskTitle}
            toggleShowTaskTitle={toggleShowTaskTitle}
            taskPopupId={taskPopupId}
            vectorPopupId={vectorPopupId}
            onChangeMapOptions={onChangeMapOptions}
            onSelectedPanel={onSelectedPanel}
            isShowZoneNameVisibility={isShowZoneNameVisibility}
            setIsShowZoneNameVisibility={setIsShowZoneNameVisibility}
            isShowAreaNameVisibility={isShowAreaNameVisibility}
            setIsShowAreaNameVisibility={setIsShowAreaNameVisibility}
            showAllZones={showAllZones}
            showAllAreas={showAllAreas}
            setShowAllAreas={setShowAllAreas}
            setShowAllZones={setShowAllZones}
            showMapAccordion={false}
            showZonesAccordion={true}
            showAreasAccordion={true}
            showTasksAccordion={true}
            zoneId={zoneId}
            mapInfo={mapInfo}
            onCenterMap={setToBrowserLocation}
            mapOptions={getMapLabel}
            currentImageResource={
              assetMap
                ? GetResourceValue(state?.gameDocument!, assetMap!.imageResId!)
                : ''
            }
            onSelectAllAssets={(value) => setSelectAllAssets(value)}
            selectedAllObjectType={selectAllAssets}
            removeTaskOverlay={removeTaskOverlay}
            updateTaskOverlayPosition={updateTaskOverlayPosition as any}
          />

          {entityZoneEditorIsVisible && (
            <ZoneEditorWindow
              toggleDialog={toggleZoneEntityEditor}
              onSubmit={handleZoneEntityEditorSubmit}
              onClose={toggleZoneEntityEditor}
              editorEntity={entityZoneEditorValue!}
              editorMode={'basic'}
              editorResource={[]}
            />
          )}

          {entityAreaEditorIsVisible && (
            <AreaEditorWindow
              toggleDialog={toggleAreaEntityEditor}
              onSubmit={handleAreaEntityEditorSubmit}
              onClose={toggleAreaEntityEditor}
              editorEntity={{
                isNew: true,
                entity: {
                  id: '',
                  name: '',
                  description: '',
                  titleResId: titleGuid
                }
              }}
              editorMode={'basic'}
              editorResource={[
                {
                  isNew: true,
                  entity: titleResource
                }
              ]}
            />
          )}

          {entityTaskEditorIsVisible && (
            <TaskEditorWindow
              toggleDialog={toggleTaskEntityEditor}
              onSubmit={handleTaskEntityEditorSubmit}
              onSelectedExistingTask={handleSelectedExistingTask}
              showExistingTask={true}
              onClose={toggleTaskEntityEditor}
              editorEntity={entityTaskEditorValue!}
              editorMode={'basic'}
              editorResource={resourceEditorValue!}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default GameMapZone;
